function renderGenDeleteBulkContentServerActionTemplate({
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  lifecycleHookImport,
  ctxSingular,
  ctxPlural,
  ctxPluralText,
  beforeHook,
  afterHook
}: {
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  lifecycleHookImport: string
  ctxSingular: string
  ctxPlural: string
  ctxPluralText: string
  beforeHook: string
  afterHook: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctxTableVar} } from '@admin/db/schema'
import { inArray } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { deleteEntityVersions, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
${lifecycleHookImport}
import type { ${ctxSingular}DeleteResult } from './types'

export async function deleteBulk${ctxPlural}(ids: string[]): Promise<${ctxSingular}DeleteResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])
  if (ids.length === 0) return { success: false, error: 'No items selected for deletion' }

  const now = new Date().toISOString()
  const auditRequestMetadata = await getAuditRequestMetadata()
  const lifecycleContexts = ids.map((id) => ({
    user: actor,
    entityType: '${ctxPluralText}',
    timestamp: now,
    sourceType: 'admin_ui' as const,
    ipAddress: auditRequestMetadata.ipAddress,
    userAgent: auditRequestMetadata.userAgent,
    entityId: id,
    changedFields: []
  }))

  for (const lifecycleContext of lifecycleContexts) {
    ${beforeHook}
  }

  try {
    const deletedRows = await db.transaction(async (tx) => {
      await deleteEntityVersions(tx, '${ctxPluralText}', ids)
      return tx.delete(${ctxTableVar}).where(inArray(${ctxTableVar}.id, ids)).returning()
    })
    const deletedRowsById = new Map(
      deletedRows.map((row) => [row.id, row as unknown as Record<string, unknown>])
    )
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    for (const id of ids) {
      ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(id))
      ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPluralText}', id))
    }
    for (const lifecycleContext of lifecycleContexts) {
      ${afterHook}
    }
    return { success: true }
  } catch (error) {
    console.error('Error deleting ${ctxPluralText}:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to delete ${ctxPluralText}' }
  }
}
`
}

function renderGenDeleteBulkContentTemplate(): string {
  return `{
        ...lifecycleContext,
        data: deletedRowsById.get(lifecycleContext.entityId)
      }`
}

export function renderGenDeleteBulkContentRenderer(
  request:
    | {
        kind: 'renderGenDeleteBulkContentServerActionTemplate'
        props: {
          ctxTableVar: string
          ctxCacheInvalidationFn: string
          ctxCamelPlural: string
          lifecycleHookImport: string
          ctxSingular: string
          ctxPlural: string
          ctxPluralText: string
          beforeHook: string
          afterHook: string
        }
      }
    | { kind: 'renderGenDeleteBulkContentTemplate' }
): string {
  switch (request.kind) {
    case 'renderGenDeleteBulkContentServerActionTemplate':
      return renderGenDeleteBulkContentServerActionTemplate(request.props)
    case 'renderGenDeleteBulkContentTemplate':
      return renderGenDeleteBulkContentTemplate()
  }

  throw new Error('Unsupported renderer request')
}
